/**
 * Format file size
 * @param bytes Byte count
 * @param decimals Decimal places
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Get file extension
 * @param filename File name
 * @returns File extension
 */
export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return filename.substring(lastDotIndex + 1).toLowerCase();
};

/**
 * Check if file is image
 * @param filename File name or path
 * @returns Whether it is an image file
 */
export const isImageFile = (filename: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const extension = getFileExtension(filename);
  return imageExtensions.includes(extension);
};

/**
 * Check if file is video
 * @param filename File name or path
 * @returns Whether it is a video file
 */
export const isVideoFile = (filename: string): boolean => {
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
  const extension = getFileExtension(filename);
  return videoExtensions.includes(extension);
};

/**
 * Check if file is audio
 * @param filename File name or path
 * @returns Whether it is an audio file
 */
export const isAudioFile = (filename: string): boolean => {
  const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];
  const extension = getFileExtension(filename);
  return audioExtensions.includes(extension);
};

/**
 * Check if file is document
 * @param filename File name or path
 * @returns Whether it is a document file
 */
export const isDocumentFile = (filename: string): boolean => {
  const documentExtensions = [
    'doc',
    'docx',
    'pdf',
    'txt',
    'md',
    'rtf',
    'odt',
    'xlsx',
    'xls',
    'ppt',
    'pptx',
    'hwp',
    'hwpx',
  ];
  const extension = getFileExtension(filename);
  return documentExtensions.includes(extension);
};

/**
 * Check if file is compressed
 * @param filename File name or path
 * @returns Whether it is a compressed file
 */
export const isCompressedFile = (filename: string): boolean => {
  const compressedExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'];
  const extension = getFileExtension(filename);
  return compressedExtensions.includes(extension);
};

/**
 * Create file URL
 * @param blob Blob or File object
 * @returns File URL
 */
export const createFileUrl = (blob: Blob): string => {
  return URL.createObjectURL(blob);
};

/**
 * Revoke file URL
 * @param url File URL
 */
export const revokeFileUrl = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * Download file
 * @param blob File Blob object
 * @param filename File name
 */
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = createFileUrl(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  revokeFileUrl(url);
};
